import React from "react";
import Link from "gatsby-link";
import { shade } from "polished";
import styled, { ThemeProvider } from "styled-components";
import { Box, Flex } from "rebass";
import { TagList, Tag } from "components";
import Text from "./Text";
import H4 from "./Headings/H4";
import {
  Volume2,
  ExternalLink,
  Book,
  BookOpen,
  Video,
  ArrowUpRight,
  Link as IconLink,
} from "react-feather";
import theme from "../theme.js";
import invertTheme from "../invertTheme.js";

const Action = styled(Box)`
  text-decoration: none;
  cursor: pointer;
  opacity: 0;
  animation: resource-card-fade-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  will-change: opacity;

  @keyframes resource-card-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  &:hover {
    .resource-card__tile {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
      transform: translateY(-4px);
    }
    .resource-card__title {
      box-shadow: inset 0 -1px 0 ${(props) => props.theme.colors.grey[0]};
    }
    .resource-card__corner-icon {
      opacity: 0.5;
      transform: translateY(0);
    }
  }
  &:active {
    .resource-card__tile {
      box-shadow: none;
      transform: translateY(-2px);
    }
    .resource-card__title {
      box-shadow: inset 0 -1px 0 ${(props) => props.theme.colors.grey[1]};
    }
  }

  * {
    text-decoration: none;

    &::selection {
      color: ${(props) => props.theme.colors.grey[2]};
      background-color: ${(props) =>
        props.theme.colors[props.cardColor] &&
        shade(0.9375, props.theme.colors[props.cardColor])};
    }
  }
`;

Action.defaultProps = {
  is: "a",
};

const Wrapper = styled(Box)``;

const Tile = styled(Box)`
  position: relative;
  width: 100%;
  height: ${96 + 24}px;
  background-color: ${(props) => props.theme.colors[props.cardColor]};
  transition: transform 500ms cubic-bezier(0.23, 1, 0.32, 1),
    box-shadow 500ms cubic-bezier(0.23, 1, 0.32, 1);
  will-change: transform;
  user-select: none;
`;

const CornerIcon = styled(Box)`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 24px;
  height: 24px;
  background-color: ${(props) => props.theme.colors.grey[2]};
  border-radius: 9999px;
  opacity: 0;
  transform: translateY(4px);
  transition: transform 500ms cubic-bezier(0.23, 1, 0.32, 1),
    opacity 500ms cubic-bezier(0.23, 1, 0.32, 1);

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    stroke: ${(props) => props.theme.colors[props.cardColor]};
  }
`;

const TileContent = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Type = styled(H4)`
  text-align: center;
  color: #fff;
`;

Type.defaultProps = {
  is: "div",
};

const Icon = styled(Box)`
  position: relative;
  left: 50%;
  width: 48px;
  height: 48px;
  transform: translateX(-50%);

  > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const Content = styled(Box)`
  width: 100%;
  margin-top: ${(props) => props.theme.space[2]}px;
`;

const Title = styled(Text)`
  padding-bottom: 4px;
  font-size: 18px;
  font-weight: 600;

  > span {
    box-shadow: inset 0 -1px 0 transparent;
    transition: box-shadow 500ms cubic-bezier(0.23, 1, 0.32, 1);
    will-change: box-shadow;
  }
`;

const determineColor = function (region) {
  let color = "violet";
  if (
    region === "update" ||
    region === "institution" ||
    region === null ||
    region === undefined
  ) {
    color = "ash";
  }
  if (
    region === "middle-east-and-north-africa" ||
    region === "oriente-medio-y-norte-de-africa" ||
    region === "الشرق-الاوسط-وشمات-افريقيا"
  ) {
    color = "orange";
  }
  if (region === "africa" || region === "africa1" || region === "افريقيا") {
    color = "green";
  }
  if (
    region === "latin-america" ||
    region === "america-latina-y-el-caribe" ||
    region === "امريكا-اللاتينية"
  ) {
    color = "blue";
  }
  if (region === "asia" || region === "asia1" || region === "اسيا") {
    color = "pink";
  }
  if (
    region === "europe--central-asia" ||
    region === "europa-y-asia-central" ||
    region === "اوروبا-واسيا-الوسطى"
  ) {
    color = "yellow";
  }
  return color;
};

const translatedTitles = {
  Newsletter: {
    "en-us": "Newsletter",
    "ar-eg": "نشرة إخبارية",
    "es-mx": "Boletine",
  },
  "Partner Website": {
    "en-us": "Partner Website",
    "ar-eg": "موقع مشارك المركز",
    "es-mx": "Página web",
  },
  Website: {
    "en-us": "Partner Website",
    "ar-eg": "موقع مشارك المركز",
    "es-mx": "Página web",
  },
  Audio: {
    "en-us": "Audio",
    "ar-eg": "صوت",
    "es-mx": "Audio",
  },
  Video: {
    "en-us": "Video",
    "ar-eg": "فيديو",
    "es-mx": "Video",
  },
  Document: {
    "en-us": "Document",
    "ar-eg": "وثيقة",
    "es-mx": "Documento",
  },
};

const ResourceCard = (props) => {
  if (props.object) {
    return (
      <ThemeProvider theme={theme}>
        <Action
          className={props.className}
          style={props.style}
          href={
            props.object.data.resource_link
              ? props.object.data.resource_link.url
              : ""
          }
          target="_blank"
          cardColor={determineColor(
            props.object.data.region ? props.object.data.region.uid : ""
          )}
          aria-label={
            props.object.data.title[0] ? props.object.data.title[0].text : ""
          }
        >
          <Wrapper>
            <Tile
              className="resource-card__tile"
              cardColor={determineColor(
                props.object.data.region ? props.object.data.region.uid : ""
              )}
            >
              <CornerIcon
                className="resource-card__corner-icon"
                cardColor={determineColor(
                  props.object.data.region ? props.object.data.region.uid : ""
                )}
              >
                <ArrowUpRight size={16} strokeWidth={2} title="External Link" />
              </CornerIcon>
              <TileContent>
                {props.object.data.post_type === "Audio" && (
                  <Icon>
                    <Volume2
                      size={32}
                      color="white"
                      strokeWidth={1.5}
                      title={
                        props.lang === "en-us"
                          ? "Audio"
                          : props.lang === "ar-eg"
                          ? "موقع مشارك المركز"
                          : "Audio"
                      }
                    />
                  </Icon>
                )}
                {props.object.data.post_type === "Video" && (
                  <Icon>
                    <Video
                      size={32}
                      color="white"
                      strokeWidth={1.5}
                      title={
                        props.lang === "en-us"
                          ? "Video"
                          : props.lang === "ar-eg"
                          ? "موقع مشارك المركز"
                          : "Video"
                      }
                    />
                  </Icon>
                )}
                {props.object.data.post_type === "Document" && (
                  <Icon>
                    <BookOpen
                      size={32}
                      color="white"
                      strokeWidth={1.5}
                      title={
                        props.lang === "en-us"
                          ? "Document"
                          : props.lang === "ar-eg"
                          ? "موقع مشارك المركز"
                          : "Documento"
                      }
                    />
                  </Icon>
                )}
                {props.object.data.post_type === "Newsletter" && (
                  <Icon>
                    <Book
                      size={32}
                      color="white"
                      strokeWidth={1.5}
                      title={
                        props.lang === "en-us"
                          ? "Newsletter"
                          : props.lang === "ar-eg"
                          ? "موقع مشارك المركز"
                          : "Boletine"
                      }
                    />
                  </Icon>
                )}
                {(props.object.data.post_type === "Partner Website" ||
                  props.object.data.post_type === "Website") && (
                  <Icon>
                    <IconLink
                      size={32}
                      color="white"
                      strokeWidth={1.5}
                      title={
                        props.lang === "en-us"
                          ? "Partner Website"
                          : props.lang === "ar-eg"
                          ? "موقع مشارك المركز"
                          : "Página web"
                      }
                    />
                  </Icon>
                )}
                <Type
                  children={
                    translatedTitles[props.object.data.post_type]
                      ? translatedTitles[props.object.data.post_type][
                          props.lang
                        ]
                      : ""
                  }
                />
              </TileContent>
            </Tile>
            <Content>
              <Title>
                <span
                  className="resource-card__title"
                  children={
                    props.object.data.title[0]
                      ? props.object.data.title[0].text
                      : ""
                  }
                />
              </Title>
              {/* <Text
                children={
                  props.object.data.resource_description[0]
                    ? props.object.data.resource_description[0].text
                    : ""
                }
                pb={2}
                fontSize={1}
              /> */}
              {/* <TagList>
                <Tag children="Asia" />
                <Tag children="Environment & Climate" />
                <Tag children="World Bank Group" />
                <Tag children="GMR Kamalanga Energy Limited, Thermal Power Project" />
              </TagList> */}
            </Content>
          </Wrapper>
        </Action>
      </ThemeProvider>
    );
  } else {
    return null;
  }
};

ResourceCard.defaultProps = {
  title: "Resource Name",
  cardColor: "yellow",
};

export default ResourceCard;



// WEBPACK FOOTER //
// ./src/components/ResourceCard.js